export default function useEmail() {
  const emailTemplates = {
    touchpoint1: {
      name: 'Touchpoint 1',
      subject: 'Following Up on Your Planning Guide',
      body: `Good {timeOfDay},\n
We're delighted to hear about your interest in our pavers and appreciate you requesting our planning guide. I wanted to personally check in and see if you've received the guide. If you have any questions about it or our extensive range of pavers and walls, please don't hesitate to reach out. We're here to help in any way we can.`,
    },
    touchpoint2: {
      name: 'Touchpoint 2',
      subject: 'Just Checking In - Ready to Explore Your Options?',
      body: `Hello {name}\n
I just wanted to reach out to check in with you to see if you need any further information on the planning guide or on any of our products.\n
To further inspire your project, we invite you to visit our expansive 10,000 sq ft display. It's a wonderful opportunity to experience the quality and variety of our products firsthand.\n
We look forward to hearing from you and helping you create a beautiful and lasting outdoor space.`,
    },
    touchpoint3: {
      name: 'Touchpoint 3',
      subject: 'Let Us Help You Find the Perfect Paver',
      body: `Hey {name}\n
I just wanted to touch base and see if you'd be open to scheduling a time to visit our display area. One of our knowledgeable sales reps would be happy to walk you through our selection of pavers and walls to help you find the best fit for your project.\n
While you're here, if something catches your eye, I'd be glad to provide you with a few free samples to take home. This way, you can see how the paver or block looks within your own landscape before making a decision.\n
Let me know what day and time works best for you—I'd be happy to set it up!\n
Looking forward to hearing from you,`,
    },
    touchpoint4: {
      name: 'Touchpoint 4',
      subject: "Just Checking In - How's Your Project Coming Along?",
      body: `Hey {name}\n
I just wanted to check in and see if you're still planning on moving forward with your project. If you are, we'd love to assist you in choosing the perfect products for your needs.\n
We understand that schedules can get busy, so if finding time to meet with one of our sales reps is tricky, feel free to stop by our outdoor display anytime— its open 24/7. This gives you the flexibility to check out our pavers and walls at your own convenience.\n
No matter what stage you're at, we're here to support you with any questions or guidance you need. Don't hesitate to reach out—we're happy to help!`,
    },
    paverQuiz: {
      name: 'Paver Quiz Follow-Up',
      subject: 'Following Up on Your Paver Quiz',
      body: `Hi {name},\n
Thank you for your interest in our products! We noticed that you recently took our paver quiz and wanted to reach out to see if you have any questions or if there's anything we can help you with.\n
Feel free to reply to this email or give us a call at 503-623-9084 — we'd love to help you find the perfect fit for your project.\n
Looking forward to hearing from you!`,
    },
    contractorList: {
      name: 'Contractor List',
      subject: 'Recommended Contractors for Your Project',
      body: `Hi {name},\n\nThank you for your interest in our products. As discussed, I'm sending you a list of contractors who can help with your project:\n\n{contractorList}\n\nPlease let me know if you have any questions or need additional information.\n\nBest regards,`,
    },
  };

  const isAfternoon = () => {
    const hours = new Date().getHours();
    return hours >= 12;
  };

  const processTemplate = (template, params = {}) => {
    let processed = template;

    // Replace greeting based on time of day
    processed = processed.replace(/{timeOfDay}/g, isAfternoon() ? 'Afternoon' : 'Morning');

    // Replace all placeholders in the format {key} with corresponding values
    Object.entries(params).forEach(([key, value]) => {
      const regex = new RegExp(`{${key}}`, 'g');
      processed = processed.replace(regex, value || '');
    });

    return processed;
  };

  const createMailtoLink = ({ email, name, subject, body, cc = [], bcc = [] }) => {
    let queryParts = [];

    if (subject) queryParts.push(`subject=${encodeURIComponent(subject)}`);
    if (body) queryParts.push(`body=${encodeURIComponent(body)}`);
    if (cc.length > 0) queryParts.push(`cc=${cc.join(',')}`);
    if (bcc.length > 0) queryParts.push(`bcc=${bcc.join(',')}`);

    const queryString = queryParts.join('&');
    return `mailto:${name ? name + ' ' : ''}<${email}>${queryString ? '?' + queryString : ''}`;
  };

  const formatContractorList = (contractors = []) => {
    if (!contractors || contractors.length === 0) return '';

    return contractors
      .map((contractor) => {
        const details = [];
        if (contractor.name) details.push(`• ${contractor.name}${contractor.certified ? ' (Certified)' : ''}`);
        if (contractor.phone) details.push(`  Phone: ${contractor.phone}`);
        if (contractor.email) details.push(`  Email: ${contractor.email}`);

        // Address handling
        const addressParts = [];
        if (contractor.line1) addressParts.push(contractor.line1);
        if (contractor.line2) addressParts.push(contractor.line2);
        if (contractor.city && contractor.state) {
          const cityStateZip = `${contractor.city}, ${contractor.state}${contractor.zip ? ' ' + contractor.zip : ''}`;
          addressParts.push(cityStateZip);
        }

        if (addressParts.length > 0) {
          details.push(`  Address: ${addressParts.join(', ')}`);
        } else if (contractor.address) {
          details.push(`  Address: ${contractor.address}`);
        }

        return details.join('\n');
      })
      .join('\n\n');
  };

  const sendEmail = ({ email, name, templateName, customParams = {}, selectedContractors = [] }) => {
    if (!email) {
      console.error('Email address is required');
      return false;
    }

    const template = emailTemplates[templateName];
    if (!template && !customParams.subject) {
      console.error('Invalid template name and no custom subject provided');
      return false;
    }

    const subject = customParams.subject || template?.subject || '';
    let body = customParams.body || template?.body || '';

    // Add contractor list if using the contractorList template
    if (templateName === 'contractorList' && selectedContractors && selectedContractors.length > 0) {
      customParams.contractorList = formatContractorList(selectedContractors);
    }

    // Process the template to replace placeholders
    body = processTemplate(body, { name, ...customParams });

    const cc = customParams.cc || [];
    const bcc = customParams.bcc || [];

    const mailtoLink = createMailtoLink({ email, name, subject, body, cc, bcc });

    try {
      window.location.href = mailtoLink;
      return true;
    } catch (error) {
      console.error('Failed to open email client', error);
      return false;
    }
  };

  return {
    emailTemplates,
    createMailtoLink,
    sendEmail,
    processTemplate,
    isAfternoon,
    formatContractorList,
  };
}
